import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Stack, TextField, Typography,
} from '@mui/material';
import {
  ExpandMoreOutlined as ExpandIcon,
  ChatBubbleOutlineOutlined as MessageIcon,
} from '@mui/icons-material';

import HiringService, { MessageTemplate } from '../../api/services/hiring.service';

/**
 * The words that go to candidates, editable.
 *
 * The six messages ship written — they are the pilot's own, rewritten —
 * and most companies will never touch them. But the rate, the schedule and
 * the tone are a company's own, and "we cannot say that" is not something
 * a product should answer with a support ticket.
 *
 * What is editable is deliberately only the WORDING. When each message is
 * the one to send stays with the engine: a company changing how it speaks
 * is a copy decision, and a company changing when a background-check nudge
 * fires is a process decision nobody has asked for and which would quietly
 * break the thing the feature is for.
 *
 * Reset deletes the override rather than restoring a copy of the default,
 * so a company that has never edited a message keeps getting improvements
 * to it — and one that has stays exactly where it put itself.
 */

const MessageTemplates: React.FC<{
  templates: MessageTemplate[];
  placeholders: string[];
  onChanged: (templates: MessageTemplate[]) => void;
}> = ({ templates, placeholders, onChanged }) => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const open = (template: MessageTemplate) => {
    const next = openKey === template.key ? null : template.key;
    setOpenKey(next);
    setDraft(next ? template.body : '');
    setError(null);
    setSaved(null);
  };

  const save = async (template: MessageTemplate, body: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await HiringService.saveTemplate(template.key, body);
      onChanged(result.templates);
      setSaved(template.key);
      if (!body) setOpenKey(null);
    } catch (err: any) {
      // The server refuses a {{placeholder}} nothing fills, and says which
      // one — that message is more useful than "could not save".
      setError(err?.response?.data?.error || err?.response?.data?.message || 'That message could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
        <MessageIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        <Typography variant="subtitle2">Messages to candidates</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The text offered on a candidate&apos;s page, ready to copy. Edit the wording
        to sound like you; when each one is offered stays as it is.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack gap={1}>
        {templates.map((template) => {
          const isOpen = openKey === template.key;
          return (
            <Box
              key={template.key}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                onClick={() => open(template)}
                sx={{
                  px: 2, py: 1.25, cursor: 'pointer',
                  bgcolor: isOpen ? 'action.hover' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{template.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{template.when}</Typography>
                </Box>
                {!template.isDefault && (
                  <Chip size="small" variant="outlined" label="Edited" sx={{ height: 20, fontSize: 11 }} />
                )}
                {saved === template.key && (
                  <Typography variant="caption" color="text.secondary">Saved</Typography>
                )}
                <ExpandIcon
                  fontSize="small"
                  sx={{
                    color: 'text.disabled',
                    transform: isOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 150ms',
                  }}
                />
              </Stack>

              {isOpen && (
                <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                  <TextField
                    multiline
                    fullWidth
                    minRows={6}
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); setSaved(null); }}
                    inputProps={{ 'aria-label': `${template.label} message` }}
                    sx={{ '& .MuiInputBase-input': { fontSize: 13.5, lineHeight: 1.6 } }}
                  />

                  <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                      Fills itself in:
                    </Typography>
                    {placeholders.map((name) => (
                      <Chip
                        key={name}
                        size="small"
                        variant="outlined"
                        label={`{{${name}}}`}
                        onClick={() => { setDraft((d) => `${d}{{${name}}}`); setSaved(null); }}
                        sx={{ height: 20, fontSize: 11, fontFamily: 'monospace' }}
                      />
                    ))}
                  </Stack>

                  <Stack direction="row" gap={1} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      disableElevation
                      disabled={busy || !draft.trim() || draft === template.body}
                      onClick={() => save(template, draft)}
                    >
                      Save
                    </Button>
                    {!template.isDefault && (
                      <Button
                        size="small"
                        color="inherit"
                        disabled={busy}
                        onClick={() => save(template, '')}
                      >
                        Reset to the original
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default MessageTemplates;
